import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { useLibraryQuery } from '@/components/games/useLibraryQuery'
import { SearchField } from '@/components/ui/SearchField'
import { Logo, LogoMark } from './Logo'

const SEARCH_DEBOUNCE_MS = 150

/**
 * Searches as you type on the library (`/`). Elsewhere, e.g. mid-game, it
 * only navigates on submit so typing never interrupts a running game.
 *
 * The text lives in local state and is written to the URL after a short
 * pause; binding the input straight to the (asynchronously updated) URL
 * drops keystrokes.
 */
function HeaderSearch() {
  const onLibrary = useLocation().pathname === '/'
  const navigate = useNavigate()
  const { search, update } = useLibraryQuery()
  const urlSearch = onLibrary ? search : ''
  const [draft, setDraft] = useState(urlSearch)
  const lastSent = useRef(urlSearch)

  // Adopt changes made elsewhere (logo click, "Clear filters", back/forward).
  useEffect(() => {
    if (urlSearch !== lastSent.current) {
      lastSent.current = urlSearch
      setDraft(urlSearch)
    }
  }, [urlSearch])

  useEffect(() => {
    if (!onLibrary || draft === lastSent.current) return
    const timer = window.setTimeout(() => {
      lastSent.current = draft
      update('q', draft || null)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [draft, onLibrary, update])

  function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (onLibrary) return
    const q = draft.trim()
    navigate(q ? `/?q=${encodeURIComponent(q)}` : '/')
  }

  return (
    <form role="search" onSubmit={handleSubmit} className="w-full max-w-md">
      <SearchField
        size="sm"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onClear={() => setDraft('')}
        placeholder="Search games"
        aria-label="Search games by title"
      />
    </form>
  )
}

export function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-line bg-ink-950/85 backdrop-blur-xl backdrop-saturate-150">
      <div className="container-page flex h-14 items-center gap-4 sm:gap-6">
        <Link to="/" className="shrink-0 rounded-lg" aria-label="Zarcade — all games">
          <span className="hidden sm:block">
            <Logo />
          </span>
          <span className="block sm:hidden">
            <LogoMark />
          </span>
        </Link>
        <div className="flex min-w-0 flex-1 justify-center">
          <HeaderSearch />
        </div>
        {/* Reserved for future account controls (Sign in / profile). */}
        <div className="flex shrink-0 items-center gap-2" data-slot="account" />
      </div>
    </header>
  )
}
